import React, { useState } from 'react';
import {
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Rating,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { appointmentService } from '@services/appointment.service';
import { toastService } from '@services/toast.service';
import { AppointmentResponseDto } from '@/types/appointment.types';
import { BaseDialog } from '../common/BaseDialog';
import { ModalIndices } from '@/common/i-doc/i-doc.registry';

interface Props {
  open: boolean;
  onClose: () => void;
  appointment: AppointmentResponseDto;
  onSuccess: (updated: AppointmentResponseDto) => void;
}

const RATING_LABELS: Record<number, string> = {
  1: 'Погано',
  2: 'Задовільно',
  3: 'Добре',
  4: 'Дуже добре',
  5: 'Чудово',
};

export const ReviewDialog: React.FC<Props> = ({ open, onClose, appointment, onSuccess }) => {
  const [rating, setRating] = useState<number | null>(null);
  const [hover, setHover] = useState(-1);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setRating(null);
    setHover(-1);
    setComment('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    try {
      await appointmentService.submitReview(appointment.id, {
        rating,
        comment: comment.trim() || undefined,
      });
      toastService.success('Відгук збережено!');
      const updated: AppointmentResponseDto = {
        ...appointment,
        review: {
          id: 0,
          rating,
          comment: comment.trim() || null,
          createdAt: new Date().toISOString(),
        },
      };
      onSuccess(updated);
      handleClose();
    } catch {
      toastService.error('Не вдалося зберегти відгук.');
    } finally {
      setSubmitting(false);
    }
  };

  const activeLabel = hover > 0 ? RATING_LABELS[hover] : rating ? RATING_LABELS[rating] : '';

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title="Відгук про зустріч"
      maxWidth="xs"
      iDoc={ModalIndices.APPOINTMENT_REVIEW}
      actions={
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!rating || submitting}
        >
          {submitting ? <CircularProgress size={20} /> : 'Надіслати'}
        </Button>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {appointment.expert.name}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}>
        <Rating
          value={rating}
          onChange={(_, v) => setRating(v)}
          onChangeActive={(_, v) => setHover(v)}
          size="large"
          emptyIcon={<StarIcon style={{ opacity: 0.4 }} fontSize="inherit" />}
        />
        <Typography variant="body2" color="text.secondary" sx={{ minHeight: 20 }}>
          {activeLabel}
        </Typography>
      </Box>

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Коментар (необов'язково)"
        value={comment}
        onChange={e => setComment(e.target.value)}
        inputProps={{ maxLength: 2000 }}
      />
    </BaseDialog>
  );
};
